(() => {
  const ROOT = document.documentElement;
  const loader = document.getElementById('PageLoader');

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  const Theme = (window.Theme = window.Theme || {});

  if (!loader) {
    ROOT.classList.remove('is-page-loading');
    Theme.PageLoader = {
      play() {
        return Promise.resolve();
      },
      finish() {},
      get active() {
        return false;
      }
    };
    return;
  }

  let style = loader.dataset.style || 'slide';
  if (style === 'reveal') style = 'slide';

  let minDisplay = Number(loader.dataset.minDisplay) || 800;
  const maxWait = Number(loader.dataset.maxWait) || 8000;
  if (isDesignMode()) minDisplay = Math.max(minDisplay, 1000);

  const percentEl = loader.querySelector('[data-page-loader-percent]');
  const barEl = loader.querySelector('[data-page-loader-progress]');

  let start = 0;
  let progress = 0;
  let rafId = 0;
  let finished = false;
  let playing = false;
  let generation = 0;
  let resolvePlay = null;
  let currentPlay = null;

  const setProgress = (value) => {
    progress = Math.max(progress, Math.min(100, value));
    if (barEl) barEl.style.transform = `scaleX(${progress / 100})`;
    if (percentEl) percentEl.textContent = `${Math.round(progress)}%`;
  };

  const getExitDuration = () => {
    const raw = getComputedStyle(loader).getPropertyValue('--page-loader-exit-duration').trim();
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 700;
  };

  const revealMusic = () => {
    document.querySelectorAll('.section-music-player, .shopify-section-group-music-group').forEach((el) => {
      el.style.removeProperty('opacity');
      el.style.removeProperty('visibility');
      el.style.removeProperty('z-index');
      el.classList.remove('is-under-loader', 'is-above-loader');
    });
    document.querySelectorAll('music-player').forEach((el) => {
      el.classList.remove('is-under-loader', 'is-above-loader');
      el.style.removeProperty('opacity');
      el.style.removeProperty('visibility');
      el.style.removeProperty('z-index');
    });
  };

  const hide = () => {
    window.cancelAnimationFrame(rafId);
    ROOT.classList.remove('is-page-loading');
    document.body.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('overflow');
    loader.classList.remove('is-exiting', 'is-finishing');
    loader.hidden = true;
    loader.setAttribute('aria-hidden', 'true');
    loader.style.visibility = 'hidden';
    loader.style.pointerEvents = 'none';
    playing = false;
    finished = true;
    revealMusic();
    document.dispatchEvent(new CustomEvent('theme:pageloader:done'));
    resolvePlay?.();
    resolvePlay = null;
    currentPlay = null;
  };

  const resetVisual = () => {
    window.cancelAnimationFrame(rafId);
    progress = 0;
    finished = false;
    loader.classList.remove('is-exiting', 'is-finishing');
    if (barEl) barEl.style.transform = 'scaleX(0)';
    if (percentEl) percentEl.textContent = '0%';
    void loader.offsetWidth;
  };

  const removeLoader = (gen) => {
    if (gen !== generation) return;
    hide();
  };

  const exit = (gen) => {
    if (gen !== generation || finished) return;
    finished = true;
    window.cancelAnimationFrame(rafId);
    loader.style.pointerEvents = 'none';

    const runExit = () => {
      if (gen !== generation) return;

      if (prefersReducedMotion()) {
        if (style === 'progress') setProgress(100);
        removeLoader(gen);
        return;
      }

      if (style === 'progress') {
        loader.classList.add('is-finishing');
        setProgress(100);

        window.setTimeout(() => {
          if (gen !== generation) return;
          loader.classList.add('is-exiting');
          loader.addEventListener(
            'transitionend',
            (event) => {
              if (event.target !== loader) return;
              removeLoader(gen);
            },
            { once: true }
          );
          window.setTimeout(() => removeLoader(gen), 480);
        }, 500);
        return;
      }

      if (style === 'split') {
        loader.classList.add('is-exiting');
        const panel = loader.querySelector('.page-loader__panel--end') || loader;
        panel.addEventListener(
          'transitionend',
          (event) => {
            if (event.propertyName !== 'transform') return;
            removeLoader(gen);
          },
          { once: true }
        );
        window.setTimeout(() => removeLoader(gen), getExitDuration() + 150);
        return;
      }

      loader.classList.add('is-exiting');
      loader.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName !== 'transform') return;
          removeLoader(gen);
        },
        { once: true }
      );
      window.setTimeout(() => removeLoader(gen), getExitDuration() + 150);
    };

    window.requestAnimationFrame(runExit);
  };

  const finish = () => {
    if (!playing) return;
    const gen = generation;
    const elapsed = performance.now() - start;
    const delay = Math.max(0, minDisplay - elapsed);
    window.setTimeout(() => exit(gen), delay);
  };

  const simulateProgress = (gen) => {
    if (style !== 'progress' || finished || gen !== generation) return;
    const elapsed = performance.now() - start;
    const next = 92 * (1 - Math.exp(-elapsed / 1800));
    setProgress(next);
    rafId = window.requestAnimationFrame(() => simulateProgress(gen));
  };

  const waitForReady = (gen) => {
    const tasks = [];

    if (document.readyState === 'loading') {
      tasks.push(
        new Promise((resolve) => {
          document.addEventListener('DOMContentLoaded', resolve, { once: true });
        })
      );
    }

    tasks.push(
      new Promise((resolve) => {
        if (document.readyState === 'complete') resolve();
        else window.addEventListener('load', resolve, { once: true });
      })
    );

    if (document.fonts && document.fonts.ready) {
      tasks.push(
        Promise.race([
          document.fonts.ready,
          new Promise((resolve) => window.setTimeout(resolve, 2500))
        ])
      );
    }

    Promise.race([
      Promise.all(tasks),
      new Promise((resolve) => window.setTimeout(resolve, maxWait))
    ]).then(() => {
      if (gen === generation) finish();
    });
  };

  const play = ({ waitForPage = false } = {}) => {
    generation += 1;
    const gen = generation;
    playing = true;
    start = performance.now();
    resetVisual();
    loader.hidden = false;
    loader.setAttribute('aria-hidden', 'true');
    loader.style.visibility = 'visible';
    loader.style.pointerEvents = '';
    ROOT.classList.add('is-page-loading');

    currentPlay = new Promise((resolve) => {
      resolvePlay = resolve;
    });
    currentPlay.generation = gen;

    if (style === 'progress') simulateProgress(gen);
    if (waitForPage) waitForReady(gen);

    return currentPlay;
  };

  const finishSession = (session) => {
    const gen = typeof session === 'number' ? session : session?.generation;
    if (gen != null && gen !== generation) return;
    finish();
  };

  Theme.PageLoader = {
    play,
    finish: finishSession,
    get active() {
      return playing;
    }
  };

  play({ waitForPage: true });

  window.addEventListener(
    'pageshow',
    (event) => {
      if (!event.persisted) return;
      hide();
    },
    { once: false }
  );
})();
